import { AdminSeniorVoTeam } from "../../models/admin-senior-vo.models";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { FormEvent } from "react";
import { twMerge } from "tailwind-merge";

export type AdminSeniorVoAddMemberFormValues = { memberName: string; memberEmail: string };
export type AdminSeniorVoAddMemberFormErrors = Partial<Record<keyof AdminSeniorVoAddMemberFormValues, string>>;

interface Props {
  team: AdminSeniorVoTeam;
  values: AdminSeniorVoAddMemberFormValues;
  errors: AdminSeniorVoAddMemberFormErrors;
  saving: boolean;
  onCancel: () => void;
  onChange: (field: keyof AdminSeniorVoAddMemberFormValues, value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
}

const AdminSeniorVoAddMemberModal = ({ team, values, errors, saving, onCancel, onChange, onSubmit }: Props) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby="admin-senior-vo-add-member-title"
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6"
  >
    <section className="w-full max-w-xl rounded-[28px] bg-white shadow-[0_40px_120px_-48px_rgba(16,42,67,0.55)]">
      <header className="flex items-start justify-between gap-4 border-b border-[#E4ECE9] px-6 py-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#0B8A7C]">Team Member</p>
          <h2 id="admin-senior-vo-add-member-title" className="mt-1 text-2xl font-semibold text-[#102A43]">
            Add Member
          </h2>
          <p className="mt-2 text-sm text-[#52606D]">{team.name}</p>
        </div>
        <button
          type="button"
          aria-label="Close add member modal"
          disabled={saving}
          onClick={onCancel}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#52606D] transition-colors hover:bg-[#F7FAFC] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </header>

      <form onSubmit={onSubmit} className="px-6 py-5">
        <div className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-semibold text-[#243B53]">Team</label>
            <input
              readOnly
              value={team.name}
              className="h-12 w-full rounded-xl border border-[#D9E2EC] bg-[#F7FAFC] px-4 text-sm text-[#486581] outline-none"
            />
          </div>

          <div>
            <label htmlFor="admin-senior-vo-member-name" className="mb-2 block text-sm font-semibold text-[#243B53]">
              Member Name
            </label>
            <input
              id="admin-senior-vo-member-name"
              type="text"
              value={values.memberName}
              onChange={(event) => onChange("memberName", event.target.value)}
              className={twMerge(
                "h-12 w-full rounded-xl border bg-white px-4 text-sm text-[#102A43] outline-none transition-colors placeholder:text-[#9AA5B1] focus:border-[#0B8A7C]",
                errors.memberName ? "border-[#BF0002]" : "border-[#D9E2EC]",
              )}
              placeholder="Enter member name"
            />
            {errors.memberName && <p className="mt-2 text-sm text-[#BF0002]">{errors.memberName}</p>}
          </div>

          <div>
            <label htmlFor="admin-senior-vo-member-email" className="mb-2 block text-sm font-semibold text-[#243B53]">
              Member Email
            </label>
            <input
              id="admin-senior-vo-member-email"
              type="email"
              value={values.memberEmail}
              onChange={(event) => onChange("memberEmail", event.target.value)}
              className={twMerge(
                "h-12 w-full rounded-xl border bg-white px-4 text-sm text-[#102A43] outline-none transition-colors placeholder:text-[#9AA5B1] focus:border-[#0B8A7C]",
                errors.memberEmail ? "border-[#BF0002]" : "border-[#D9E2EC]",
              )}
              placeholder="Enter member email"
            />
            {errors.memberEmail && <p className="mt-2 text-sm text-[#BF0002]">{errors.memberEmail}</p>}
          </div>
        </div>

        <footer className="mt-8 flex items-center justify-end gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="inline-flex h-11 items-center justify-center rounded-xl border border-[#D9E2EC] bg-white px-5 text-sm font-semibold text-[#52606D] transition-colors hover:bg-[#F7FAFC] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex h-11 items-center justify-center rounded-xl bg-[#0B8A7C] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#0A796D] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Adding..." : "Add"}
          </button>
        </footer>
      </form>
    </section>
  </div>
);

export default AdminSeniorVoAddMemberModal;
